import React, { useCallback, useEffect, useState } from "react";
import { useAuthContext } from "../context/AuthContext";
import { useAlert } from "../components/AlertModal";
import {
  fetchShiftStaffs,
  createShiftStaff,
  updateShiftStaff,
  deleteShiftStaff,
  fetchShiftLocations,
} from "../api/shiftApi";
import "../styles/ShiftMasterPage.css";

function ShiftStaffMasterPage() {
  const { user: userInfo, loading } = useAuthContext();
  const { showAlert } = useAlert();

  const [items, setItems] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loadingPage, setLoadingPage] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newName, setNewName] = useState("");
  const [newDefaultLocation, setNewDefaultLocation] = useState("");
  const [newDefaultWorkTime, setNewDefaultWorkTime] = useState("");
  const [newDisplayOrder, setNewDisplayOrder] = useState(0);
  const [newIsActive, setNewIsActive] = useState(true);
  const [newNote, setNewNote] = useState("");

  const loadData = useCallback(async () => {
    if (!userInfo?.store_id) return;

    setLoadingPage(true);
    try {
      const [staffData, locationData] = await Promise.all([
        fetchShiftStaffs({ storeId: userInfo.store_id }),
        fetchShiftLocations({ storeId: userInfo.store_id }),
      ]);

      const staffList = Array.isArray(staffData)
        ? staffData
        : staffData?.results || [];

      const locationList = Array.isArray(locationData)
        ? locationData
        : locationData?.results || [];

      setItems(staffList);
      setLocations(locationList);
    } catch (error) {
      console.error("スタッフマスター取得エラー:", error);
      showAlert("スタッフ一覧の取得に失敗しました。");
    } finally {
      setLoadingPage(false);
    }
  }, [userInfo?.store_id, showAlert]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreate = async () => {
    if (!newName.trim()) {
      showAlert("名前を入力してください。");
      return;
    }

    setSaving(true);
    try {
      await createShiftStaff({
        store: userInfo.store_id,
        name: newName.trim(),
        default_location: newDefaultLocation ? Number(newDefaultLocation) : null,
        default_work_time: newDefaultWorkTime || "",
        display_order: Number(newDisplayOrder) || 0,
        is_active: !!newIsActive,
        note: newNote || "",
      });

      setNewName("");
      setNewDefaultLocation("");
      setNewDefaultWorkTime("");
      setNewDisplayOrder(0);
      setNewIsActive(true);
      setNewNote("");

      await loadData();
      showAlert("スタッフを追加しました。");
    } catch (error) {
      console.error("スタッフ追加エラー:", error);
      const detail =
        error?.details?.detail ||
        error?.details?.error ||
        "スタッフ追加に失敗しました。";
      showAlert(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = (id, key, value) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              [key]: value,
            }
          : item
      )
    );
  };

  const handleUpdate = async (item) => {
    try {
      await updateShiftStaff(item.id, {
        name: item.name,
        default_location: item.default_location
          ? Number(item.default_location)
          : null,
        default_work_time: item.default_work_time || "",  
        display_order: Number(item.display_order) || 0,
        is_active: !!item.is_active,
        note: item.note || "",
      });

      await loadData();
      showAlert("更新しました。");
    } catch (error) {
      console.error("スタッフ更新エラー:", error);
      const detail =
        error?.details?.detail ||
        error?.details?.error ||
        "更新に失敗しました。";
      showAlert(detail);
    }
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("削除しますか？");
    if (!ok) return;

    try {
      await deleteShiftStaff(id);
      await loadData();
      showAlert("削除しました。");
    } catch (error) {
      console.error("スタッフ削除エラー:", error);
      const detail =
        error?.details?.detail ||
        error?.details?.error ||
        "削除に失敗しました。";
      showAlert(detail);
    }
  };

  if (loading || !userInfo) {
    return <div>読み込み中...</div>;
  }

  return (
    <div className="shift-master-page">
      <h2 className="shift-master-title">スタッフマスター</h2>

      <div className="shift-master-card">
        <h3 className="shift-master-subtitle">新規追加</h3>

        <div className="shift-master-form-row">
          <input
            type="text"
            className="shift-master-input"
            placeholder="名前"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />

          <select
            className="shift-master-input"
            value={newDefaultLocation}
            onChange={(e) => setNewDefaultLocation(e.target.value)}
          >
            <option value="">基本勤務場所なし</option>
            {locations.map((location) => (
              <option key={location.id} value={location.id}>
                {location.short_name || location.name}
              </option>
            ))}
          </select>

          <input
            type="text"
            className="shift-master-input"
            placeholder="基本勤務時間 例: 12:00〜18:00"
            value={newDefaultWorkTime}
            onChange={(e) => setNewDefaultWorkTime(e.target.value)}
          />

          <input
            type="number"
            className="shift-master-input small"
            placeholder="表示順"
            value={newDisplayOrder}
            onChange={(e) => setNewDisplayOrder(e.target.value)}
          />

          <label className="shift-master-checkbox">
            <input
              type="checkbox"
              checked={newIsActive}
              onChange={(e) => setNewIsActive(e.target.checked)}
            />
            有効
          </label>

          <input
            type="text"
            className="shift-master-input"
            placeholder="メモ"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />

          <button
            type="button"
            className="shift-master-btn"
            onClick={handleCreate}
            disabled={saving}
          >
            {saving ? "追加中..." : "追加"}
          </button>
        </div>
      </div>

      <div className="shift-master-card">
        <h3 className="shift-master-subtitle">一覧</h3>

        {loadingPage ? (
          <div>読み込み中...</div>
        ) : (
          <div className="shift-master-table-wrap">
            <table className="shift-master-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>名前</th>
                  <th>基本勤務場所</th>
                  <th>基本勤務時間</th>
                  <th>表示順</th>
                  <th>有効</th>
                  <th>メモ</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>

                    <td>
                      <input
                        type="text"
                        className="shift-master-input"
                        value={item.name || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "name", e.target.value)
                        }
                      />
                    </td>

                    <td>
                      <select
                        className="shift-master-input"
                        value={item.default_location ?? ""}
                        onChange={(e) =>
                          handleFieldChange(
                            item.id,
                            "default_location",
                            e.target.value ? Number(e.target.value) : null
                          )
                        }
                      >
                        <option value="">なし</option>
                        {locations.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.short_name || location.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="shift-master-input"
                        value={item.default_work_time || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "default_work_time", e.target.value)
                        }
                        placeholder="例: 12:00〜18:00"
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="shift-master-input small"
                        value={item.display_order ?? 0}
                        onChange={(e) =>
                          handleFieldChange(
                            item.id,
                            "display_order",
                            e.target.value
                          )
                        }
                      />
                    </td>

                    <td>
                      <label className="shift-master-checkbox">
                        <input
                          type="checkbox"
                          checked={!!item.is_active}
                          onChange={(e) =>
                            handleFieldChange(
                              item.id,
                              "is_active",
                              e.target.checked
                            )
                          }
                        />
                        有効
                      </label>
                    </td>

                    <td>
                      <input
                        type="text"
                        className="shift-master-input"
                        value={item.note || ""}
                        onChange={(e) =>
                          handleFieldChange(item.id, "note", e.target.value)
                        }
                      />
                    </td>

                    <td className="shift-master-actions">
                      <button
                        type="button"
                        className="shift-master-btn"
                        onClick={() => handleUpdate(item)}
                      >
                        更新
                      </button>

                      <button
                        type="button"
                        className="shift-master-btn danger"
                        onClick={() => handleDelete(item.id)}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}

                {items.length === 0 && (
                  <tr>
                    <td colSpan="7">データがありません。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ShiftStaffMasterPage;